import React from 'react';
import { Monitor, Mic, Video, SplitSquareHorizontal, Activity, Wifi, Clock, Cpu, Volume2 } from 'lucide-react';
import { SystemState, PartitionState } from '../types/crestron';
interface StatusStripProps {
  state: SystemState;
  onPartitionToggle: () => void;
}
export function StatusStrip({
  state,
  onPartitionToggle
}: StatusStripProps) {
  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return <div className="h-[56px] bg-gradient-to-b from-neutral-900 to-neutral-900/95 border-b border-neutral-700/50 flex items-center justify-between px-4 select-none backdrop-blur-sm" data-id="element-254">
      {/* Left: System Identity & Network */}
      <div className="flex items-center gap-3" data-id="element-255">
        <div className="h-8 w-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-lg shadow-blue-900/50" data-id="element-256">
          <span className="font-bold text-white text-xs" data-id="element-257">AB</span>
        </div>
        <div data-id="element-258">
          <h1 className="text-sm font-bold text-white leading-none tracking-tight" data-id="element-259">
            AIRBUS BANGALORE
          </h1>
          <div className="flex items-center gap-2 mt-0.5" data-id="element-260">
            <span className="text-[10px] text-gray-400" data-id="element-261">Townhall AV</span>
            <div className="flex items-center gap-1" data-id="element-262">
              <Wifi size={10} className="text-green-400" data-id="element-263" />
              <span className="text-[10px] text-green-400 font-mono" data-id="element-264">CP4</span>
            </div>
          </div>
        </div>
      </div>

      {/* Center: Detailed Status Indicators */}
      <div className="flex items-center gap-3" data-id="element-265">
        {/* Partition Status */}
        <button onClick={onPartitionToggle} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 transition-all cursor-pointer hover:scale-105 active:scale-95 ${state.partition === PartitionState.Open ? 'bg-green-900/40 border-green-700 text-green-300 shadow-[0_0_12px_rgba(34,197,94,0.2)] hover:bg-green-900/50' : 'bg-amber-900/40 border-amber-700 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.2)] hover:bg-amber-900/50'}`} data-id="element-266">
          <SplitSquareHorizontal size={14} strokeWidth={2.5} data-id="element-267" />
          <div className="flex flex-col" data-id="element-268">
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-70 leading-none" data-id="element-269">
              Partition
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wide leading-none mt-0.5" data-id="element-270">
              {state.partition === PartitionState.Open ? 'UNIFIED' : 'SPLIT'}
            </span>
          </div>
        </button>

        {/* Active Source */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-800/80 border border-neutral-700" data-id="element-271">
          <Monitor size={14} className="text-blue-400" strokeWidth={2} data-id="element-272" />
          <div className="flex flex-col" data-id="element-273">
            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none" data-id="element-274">
              Main
            </span>
            <span className="text-[10px] text-white font-bold tracking-wide leading-none mt-0.5" data-id="element-275">
              {state.mainSource}
            </span>
          </div>
        </div>

        {/* Audio Status */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-800/80 border border-neutral-700" data-id="element-276">
          <Volume2 size={14} className={state.masterVolume === 0 ? 'text-red-500' : 'text-green-400'} strokeWidth={2} data-id="element-277" />
          <div className="flex flex-col" data-id="element-278">
            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none" data-id="element-279">
              Audio
            </span>
            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-280">
              <span className={`text-[10px] font-bold tracking-wide leading-none ${state.masterVolume === 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-281">
                {state.masterVolume === 0 ? 'MUTE' : `${state.masterVolume}%`}
              </span>
              {state.masterVolume > 0 && <div className="flex gap-[1px]" data-id="element-282">
                  {[...Array(4)].map((_, i) => <div key={i} className={`w-[2px] h-2 rounded-full ${i < Math.floor(state.masterVolume / 25) ? 'bg-green-500' : 'bg-neutral-700'}`} data-id="element-283" />)}
                </div>}
            </div>
          </div>
        </div>

        {/* Camera Mode */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-800/80 border border-neutral-700" data-id="element-284">
          <Video size={14} className={state.presenterTracking ? 'text-green-400' : 'text-amber-400'} strokeWidth={2} data-id="element-285" />
          <div className="flex flex-col" data-id="element-286">
            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none" data-id="element-287">
              Camera
            </span>
            <span className={`text-[10px] font-bold tracking-wide leading-none mt-0.5 ${state.presenterTracking ? 'text-green-400' : 'text-amber-400'}`} data-id="element-288">
              {state.presenterTracking ? 'AUTO' : 'MANUAL'}
            </span>
          </div>
        </div>

        {/* Mic Status */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-800/80 border border-neutral-700" data-id="element-289">
          <Mic size={14} className={state.mics.some(m => !m.muted) ? 'text-green-400' : 'text-red-500'} strokeWidth={2} data-id="element-290" />
          <div className="flex flex-col" data-id="element-291">
            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider leading-none" data-id="element-292">
              Mics
            </span>
            <span className={`text-[10px] font-bold tracking-wide leading-none mt-0.5 ${state.mics.some(m => !m.muted) ? 'text-green-400' : 'text-red-400'}`} data-id="element-293">
              {state.mics.filter(m => !m.muted).length}/{state.mics.length}{' '}
              LIVE
            </span>
          </div>
        </div>
      </div>

      {/* Right: System Health & Time */}
      <div className="flex items-center gap-3" data-id="element-294">
        {/* DSP Status */}
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-900/30 border border-green-800/50" data-id="element-295">
          <Cpu size={12} className="text-green-400" data-id="element-296" />
          <div className="flex flex-col" data-id="element-297">
            <span className="text-[9px] font-bold text-green-500 uppercase tracking-wider leading-none" data-id="element-298">
              DSP
            </span>
            <span className="text-[10px] font-bold text-green-400 leading-none mt-0.5" data-id="element-299">
              ONLINE
            </span>
          </div>
        </div>

        {/* System Time */}
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-neutral-800/80 border border-neutral-700" data-id="element-300">
          <Clock size={12} className="text-gray-400" data-id="element-301" />
          <span className="text-xs font-mono font-bold text-white tabular-nums" data-id="element-302">
            {currentTime}
          </span>
        </div>
      </div>
    </div>;
}